import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { SEO_PAGE_HEADERS } from '~/lib/cache.server'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { ArticleSchema, ArticleMeta } from '~/components/seo-content'
import { FAQSchema, BreadcrumbSchema } from '~/components/structured-data'
import { COMPETITORS, AFFICHESERVICES } from '~/lib/competitors-data'
import { CheckCircle, ArrowRight } from 'lucide-react'

export function headers() { return SEO_PAGE_HEADERS }

export async function loader({ params }: LoaderFunctionArgs) {
  const { competitor: slug } = params
  const competitor = slug ? COMPETITORS[slug] : null
  if (!competitor) throw new Response('Comparatif non trouvé', { status: 404 })
  return { competitor }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return [{ title: 'Comparatif non trouvé' }]
  const c = data.competitor
  return [
    { title: `AfficheServices vs ${c.name} — Comparatif 2026` },
    { name: 'description', content: `AfficheServices ou ${c.name} ? Comparatif détaillé : vérification, filtre RGE, médiation, prix. 110 000+ artisans vérifiés vs ${c.proCount.split(' ').slice(0,3).join(' ')}.`.slice(0, 160) },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/vs/${c.slug}` },
  ]
}

export default function VsPage() {
  const { competitor: c } = useLoaderData<typeof loader>()

  const faqs = [
    {
      question: `AfficheServices ou ${c.name} : lequel choisir ?`,
      answer: `Si vous voulez une vérification d'identité KYC en plus du SIRET, un filtre RGE / Qualibat granulaire et une médiation gratuite incluse, AfficheServices répond à ces critères. Si la notoriété historique de ${c.name} (depuis ${c.founded}) prime, ${c.name} reste une option valide.`,
    },
    {
      question: `Combien d'artisans sur AfficheServices vs ${c.name} ?`,
      answer: `AfficheServices référence plus de 110 000 artisans en France. ${c.name} ${c.proCount.toLowerCase().startsWith('5') ? 'annonce 5 000 professionnels sur sa page d\'accueil' : 'ne publie pas son volume exact d\'artisans'}.`,
    },
    {
      question: `Le service est-il gratuit pour les particuliers sur les deux plateformes ?`,
      answer: `Oui, les deux plateformes sont gratuites pour les particuliers. AfficheServices n'exige pas d'inscription préalable et n'impose aucun engagement. ${c.name} pratique également la gratuité pour les demandes de devis.`,
    },
    {
      question: `Quelle plateforme propose un filtre RGE / Qualibat ?`,
      answer: `AfficheServices propose un filtre RGE / Qualibat / Qualifelec / Qualit'EnR activable directement sur les pages catégorie. Sur ${c.name}, ce filtre granulaire n'est pas mis en avant publiquement. Pour bénéficier de MaPrimeRénov' et CEE, l'artisan doit être certifié RGE — vérifier ce point en amont est essentiel.`,
    },
    {
      question: `Combien de devis recevoir ?`,
      answer: `AfficheServices envoie votre demande à 3 artisans qualifiés. ${c.name} en envoie ${c.responseTime.includes('5 devis') ? 'jusqu\'à 5' : 'un nombre variable'}. Plus de devis ne signifie pas toujours mieux : 3 devis bien ciblés permettent une comparaison nette sans saturation.`,
    },
  ]

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Comparatifs', url: 'https://www.afficheservices.fr/vs' },
        { name: `AfficheServices vs ${c.name}`, url: `https://www.afficheservices.fr/vs/${c.slug}` },
      ]} />
      <ArticleSchema
        title={`AfficheServices vs ${c.name} en 2026`}
        description={`Comparatif direct AfficheServices et ${c.name} sur tous les critères clés.`}
        url={`https://www.afficheservices.fr/vs/${c.slug}`}
      />
      <FAQSchema items={faqs} />

      <article className="mx-auto max-w-4xl px-4 py-10 lg:px-6">
        <div className="rounded-2xl bg-gradient-to-br from-primary to-blue-600 p-8 text-white">
          <p className="text-xs font-semibold uppercase tracking-wider text-blue-200">Comparatif direct 2026</p>
          <h1 className="mt-2 text-3xl font-bold sm:text-4xl">AfficheServices vs {c.name}</h1>
          <p className="mt-3 text-sm leading-relaxed text-blue-100 sm:text-base">
            Comparez les deux plateformes critère par critère pour choisir celle qui correspond
            le mieux à votre projet de travaux.
          </p>
        </div>

        <div className="mt-4">
          <ArticleMeta />
        </div>

        {/* TL;DR verdict */}
        <section className="mt-6 rounded-2xl border border-primary-200 bg-primary-50/40 p-6">
          <h2 className="text-lg font-bold text-slate-900">Verdict en 3 phrases</h2>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> <strong>AfficheServices</strong> gagne sur la vérification (SIRET + RC Pro + KYC), le filtre RGE et la médiation.</li>
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> <strong>{c.name}</strong> conserve l'avantage de la notoriété historique ({2026 - c.founded} ans d'existence).</li>
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Les deux sont gratuits pour le particulier ; le critère décisif est le niveau de garantie souhaité.</li>
          </ul>
        </section>

        {/* Comparison table */}
        <section className="mt-8 overflow-hidden rounded-2xl border border-border bg-white shadow-xs">
          <div className="border-b border-border px-6 py-4">
            <h2 className="text-lg font-bold text-slate-900">Comparatif détaillé</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 sm:px-6">Critère</th>
                  <th className="px-4 py-3 text-center sm:px-6">AfficheServices</th>
                  <th className="px-4 py-3 text-center sm:px-6">{c.name}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <Row label="Année de création" a="2024" b={String(c.founded)} winner="b" tag="Historique" />
                <Row label="Artisans référencés" a={AFFICHESERVICES.proCount} b={c.proCount} winner="a" tag="Volume" />
                <Row label="Vérification SIRET (SIRENE)" a="Oui, automatique" b="Probablement (non documenté en détail)" winner="a" tag="Confiance" />
                <Row label="Vérification d'identité (KYC)" a="Oui, comptes Pro et Enterprise" b="Non documenté publiquement" winner="a" tag="Confiance" />
                <Row label="Contrôle assurance RC Pro" a="Oui, vérifié" b="Non documenté publiquement" winner="a" tag="Confiance" />
                <Row label="Filtre RGE / Qualibat granulaire" a="Oui, par catégorie" b={c.rgeFilter === 'native' ? 'Oui' : 'Limité ou non visible'} winner="a" tag="Aides" />
                <Row label="Médiation gratuite litige" a="Oui, incluse" b="Non documentée publiquement" winner="a" tag="SAV" />
                <Row label="Nombre de devis comparatifs" a="3 devis" b={c.responseTime.includes('5 devis') ? '5 devis' : 'Variable'} winner="b" tag="Volume" />
                <Row label="Délai de réponse" a="48h standard, 4h urgence" b={c.responseTime} winner="a" tag="Vitesse" />
                <Row label="Prix pour le particulier" a="100 % gratuit, sans inscription" b={c.pricingClient} winner="tie" tag="Prix" />
              </tbody>
            </table>
          </div>
        </section>

        {/* Who each is best for */}
        <section className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6">
            <h3 className="text-base font-bold text-emerald-900">AfficheServices est fait pour vous si...</h3>
            <ul className="mt-3 space-y-2 text-sm text-emerald-800">
              <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0" /> Vous voulez une vérification d'identité KYC en plus du SIRET</li>
              <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0" /> Vous cherchez un artisan RGE pour bénéficier de MaPrimeRénov'</li>
              <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0" /> Vous voulez une médiation gratuite garantie en cas de litige</li>
              <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0" /> Vous préférez un large choix d'artisans (110 000+)</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-6">
            <h3 className="text-base font-bold text-amber-900">{c.name} est fait pour vous si...</h3>
            <ul className="mt-3 space-y-2 text-sm text-amber-800">
              {c.strengths.slice(0, 3).map((s, i) => (
                <li key={i} className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0" /> {s}</li>
              ))}
              <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0" /> {c.responseTime.includes('5 devis') ? 'Vous préférez recevoir 5 devis plutôt que 3' : 'Vous appréciez la marque historique'}</li>
            </ul>
          </div>
        </section>

        {/* CTA */}
        <section className="mt-8 rounded-2xl bg-primary-50 p-8 text-center">
          <h2 className="text-lg font-bold text-slate-900">Prêt à comparer en réel ?</h2>
          <p className="mt-2 text-sm text-slate-600">
            Recevez 3 devis gratuits d'artisans vérifiés sous 48h. Sans engagement, sans inscription.
          </p>
          <Link
            to="/devis-gratuit"
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press"
          >
            Demander 3 devis gratuits <ArrowRight className="h-4 w-4" />
          </Link>
        </section>

        {/* FAQ */}
        <section className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Questions fréquentes</h2>
          <div className="mt-4 space-y-4">
            {faqs.map((f, i) => (
              <details key={i} className="group border-b border-slate-100 pb-4 last:border-0">
                <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-900 hover:text-primary">
                  {f.question}
                  <span className="text-slate-300 group-open:rotate-45">+</span>
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-8">
          <Link
            to={`/alternatives/${c.slug}`}
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
          >
            Voir aussi : Alternative à {c.name} →
          </Link>
        </section>
      </article>

      <Footer />
    </div>
  )
}

function Row({ label, a, b, winner, tag }: { label: string; a: string; b: string; winner: 'a' | 'b' | 'tie'; tag: string }) {
  const winA = winner === 'a'
  const winB = winner === 'b'
  return (
    <tr>
      <td className="px-4 py-3 sm:px-6">
        <p className="font-medium text-slate-900">{label}</p>
        <p className="text-[10px] uppercase tracking-wider text-slate-400">{tag}</p>
      </td>
      <td className={`px-4 py-3 text-center sm:px-6 ${winA ? 'bg-emerald-50/50 font-semibold text-emerald-800' : 'text-slate-600'}`}>
        {winA && <CheckCircle className="mr-1 inline h-3.5 w-3.5 text-emerald-500" />}{a}
      </td>
      <td className={`px-4 py-3 text-center sm:px-6 ${winB ? 'bg-amber-50/50 font-semibold text-amber-800' : 'text-slate-500'}`}>
        {winB && <CheckCircle className="mr-1 inline h-3.5 w-3.5 text-amber-600" />}{b}
      </td>
    </tr>
  )
}
